import React, {useState} from 'react';
import {Modal, Button, Form, Spinner, Alert, InputGroup} from 'react-bootstrap';

interface AddMailingListModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (adresse: string) => Promise<void>;
  domainName: string;
}

const AddMailingListModal: React.FC<AddMailingListModalProps> = ({
  show,
  onHide,
  onAdd,
  domainName,
}) => {
  const [localPart, setLocalPart] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateLocalPart = (local: string): boolean => {
    // Validation de la partie locale uniquement
    const localPartRegex = /^[a-zA-Z0-9._-]+$/;
    return local.length > 0 && localPartRegex.test(local);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!localPart.trim()) {
      setError('Le nom de la liste est obligatoire');
      return;
    }

    if (!validateLocalPart(localPart)) {
      setError('Format invalide. Utilisez uniquement des lettres, chiffres, points, tirets et underscores');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const fullAddress = `${localPart}@${domainName}`;
      await onAdd(fullAddress);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création de la liste de diffusion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setLocalPart('');
      setError('');
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-plus me-2 text-primary"></i>
          Ajouter une liste de diffusion
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="fa-solid fa-circle-exclamation me-2"></i>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              Adresse de la liste <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="liste"
                value={localPart}
                onChange={(e) => {
                  setLocalPart(e.target.value);
                  setError('');
                }}
                isInvalid={!!error}
                autoFocus
                disabled={isLoading}
                required
              />
              <InputGroup.Text>@{domainName}</InputGroup.Text>
            </InputGroup>
            {error && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {error}
              </Form.Control.Feedback>
            )}
            <Form.Text className="text-muted">
              Saisissez uniquement la partie avant le @
            </Form.Text>
          </Form.Group>

          <div className="alert alert-light-info d-flex align-items-center">
            <i className="fa-solid fa-circle-info fs-3 me-3"></i>
            <div>
              <strong>Information :</strong> La liste sera créée vide. Vous pourrez ajouter des membres après sa création.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            <i className="fa-solid fa-xmark me-2"></i>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Création en cours...
              </>
            ) : (
              <>
                <i className="fa-solid fa-plus me-2"></i>
                Créer la liste
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddMailingListModal;
